import { Insight } from 'libs/end-to-end/src/insights';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { FocusAreaSummaryDisparity } from 'libs/end-to-end/src/summary';
import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { SelectOption } from 'apps/sigma2-ui/src/components/ui/SelectComponent/SimpleSelect';
import { SimpleSelect } from 'apps/sigma2-ui/src/components/ui/SelectComponent/SimpleSelect';
import { DisparityInsight } from 'apps/sigma2-ui/src/disparityAndOptimization/components/DisparityInsight';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { extractRootCauseTextOutputs } from 'libs/end-to-end/src/root-cause-analysis';
import { generateTextFromRootCauseTextOutputs } from 'libs/end-to-end/src/root-cause-analysis';
import { isDiversionBarGraphGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { RootCauseGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';

interface DisparitySectionProps {
  focusAreaDefinition: MyFocusAreaDefinition;
  metricDefinition: MyMetricDefinition;
  dimension: string;
  disparities: FocusAreaSummaryDisparity[];
  insights: Insight[];
  goToRootCauseAnalysis: (demographic: string) => void;
}

export const DisparitySection = ({
  focusAreaDefinition,
  metricDefinition,
  dimension,
  disparities,
  insights,
  goToRootCauseAnalysis,
}: DisparitySectionProps) => {
  const language = useLanguage();

  const {
    getPrivilegedLevelForDimension,
    getAllLevelsForDimension,
    getLevelByColumnValue,
    getDimensionByColumnHeaderValue,
  } = useDimensionConfig();

  const defaultComparisonLevelDefinition = useMemo(() => {
    const levels = getAllLevelsForDimension(dimension);
    return levels.find((level) => !level.isPrivileged);
  }, [getAllLevelsForDimension, dimension]);

  const defaultReferenceLevelDefinition = useMemo(() => {
    return getPrivilegedLevelForDimension(dimension);
  }, [getPrivilegedLevelForDimension, dimension]);

  const [
    selectedComparisonLevelDefinition,
    setSelectedComparisonLevelDefinition,
  ] = useState<LevelDefinition | undefined>(defaultComparisonLevelDefinition);
  const [
    selectedReferenceLevelDefinition,
    setSelectedReferenceLevelDefinition,
  ] = useState<LevelDefinition | undefined>(defaultReferenceLevelDefinition);

  const disparity: number | null = useMemo(() => {
    if (
      !disparities ||
      !selectedComparisonLevelDefinition ||
      !selectedReferenceLevelDefinition
    ) {
      return null;
    }

    const disparity = disparities.find(
      (d) =>
        d.demographicComparison ===
          selectedComparisonLevelDefinition.columnValue &&
        d.demographicReference === selectedReferenceLevelDefinition.columnValue,
    )?.disparity;

    return disparity ?? null;
  }, [
    disparities,
    selectedComparisonLevelDefinition,
    selectedReferenceLevelDefinition,
  ]);

  const compareAndReferenceFilterAreSame = useMemo(() => {
    if (
      !selectedComparisonLevelDefinition ||
      !selectedReferenceLevelDefinition
    ) {
      return false;
    }
    return (
      selectedComparisonLevelDefinition.columnValue ===
      selectedReferenceLevelDefinition.columnValue
    );
  }, [selectedComparisonLevelDefinition, selectedReferenceLevelDefinition]);

  const options: SelectOption<string>[] = useMemo(() => {
    if (!disparities) {
      return [];
    }

    const levelNames = disparities.flatMap((disparity) => {
      return [disparity.demographicReference, disparity.demographicComparison];
    });

    return [...new Set(levelNames)].map((levelName) => {
      const label =
        getLevelByColumnValue(dimension, levelName)?.displayName ?? '';
      return {
        value: levelName,
        label: label,
      };
    });
  }, [disparities, getLevelByColumnValue, dimension]);

  const certainty: number = useMemo(() => {
    if (
      !disparities ||
      !selectedComparisonLevelDefinition ||
      !selectedReferenceLevelDefinition
    ) {
      return 0;
    }

    if (compareAndReferenceFilterAreSame) {
      return 0;
    }

    const certainty = disparities.find(
      (d) =>
        d.demographicComparison ===
          selectedComparisonLevelDefinition.columnValue &&
        d.demographicReference === selectedReferenceLevelDefinition.columnValue,
    )?.certainty;

    return 100 - (certainty ?? 0);
  }, [
    compareAndReferenceFilterAreSame,
    disparities,
    selectedComparisonLevelDefinition,
    selectedReferenceLevelDefinition,
  ]);

  const rootCauseText = useMemo(() => {
    if (
      !selectedComparisonLevelDefinition ||
      !selectedReferenceLevelDefinition
    ) {
      return null;
    }

    const foundInsight = insights.find(
      (insight) =>
        insight.dimension === dimension &&
        insight.treatmentLevel ===
          selectedComparisonLevelDefinition.columnValue &&
        insight.referenceLevel ===
          selectedReferenceLevelDefinition.columnValue &&
        insight.groupResults.length > 0,
    );

    if (!foundInsight) {
      return null;
    }

    const metricDefinition = focusAreaDefinition.metrics.find(
      (metric) => metric.columnHeaderValue === foundInsight.metric,
    );

    if (!metricDefinition) {
      return null;
    }

    const textsUnsorted = foundInsight?.groupResults.map((groupResult) => {
      return getTextOrExplanation({
        groupResult,
        focusAreaDefinition,
        agentReference: metricDefinition.whiteLabeling.subjectPlural,
      });
    });

    const textsSorted = textsUnsorted?.sort();
    const text = textsSorted?.at(0) ?? null;

    if (!text) {
      return null;
    }

    return text;
  }, [
    dimension,
    focusAreaDefinition,
    insights,
    selectedComparisonLevelDefinition,
    selectedReferenceLevelDefinition,
  ]);

  const dimensionDisplayName =
    getDimensionByColumnHeaderValue(dimension)?.displayName || dimension;

  useEffect(() => {
    setSelectedComparisonLevelDefinition(defaultComparisonLevelDefinition);
    setSelectedReferenceLevelDefinition(defaultReferenceLevelDefinition);
  }, [defaultComparisonLevelDefinition, defaultReferenceLevelDefinition]);

  if (
    selectedComparisonLevelDefinition == undefined ||
    selectedReferenceLevelDefinition == undefined
  ) {
    return <></>;
  }

  return (
    <section className="font-sans w-full">
      <Spacing direction="vertical" size="" className="justify-between h-full">
        <Spacing direction="vertical" size="md" className="h-full">
          <div className="px-6">
            <S2Typography
              variant={'headline-5-alt'}
              translatedContent={language.focusAreaSummaryDisparityTitle({
                dimension: dimensionDisplayName,
              })}
            />
          </div>

          <Spacing direction="vertical" size="xs" className="px-6">
            <S2Typography
              variant="body-6"
              translatedContent={language.disparityBlurb1({
                metricDefinition,
              })}
            />
            <SimpleSelect
              selectedOptionValue={
                selectedComparisonLevelDefinition.columnValue || ''
              }
              options={options}
              updateOptions={(newSelection) => {
                const LevelDefinition = getLevelByColumnValue(
                  dimension,
                  newSelection,
                );
                if (!LevelDefinition) {
                  return;
                }
                setSelectedComparisonLevelDefinition(LevelDefinition);
              }}
            />
            <S2Typography
              variant="body-6"
              translatedContent={language.disparityBlurb2({
                metricDefinition,
              })}
            />
            <SimpleSelect
              selectedOptionValue={
                selectedReferenceLevelDefinition.columnValue || ''
              }
              options={options}
              updateOptions={(newSelection) => {
                const LevelDefinition = getLevelByColumnValue(
                  dimension,
                  newSelection,
                );
                if (!LevelDefinition) {
                  return;
                }
                setSelectedReferenceLevelDefinition(LevelDefinition);
              }}
            />
          </Spacing>

          <DisparityInsight
            language={language}
            rootCauseText={rootCauseText}
            disparity={disparity}
            certainty={certainty}
            compareAndReferenceFilterAreSame={compareAndReferenceFilterAreSame}
            selectedLevelDefinition={selectedComparisonLevelDefinition}
            goToRootCauseAnalysis={goToRootCauseAnalysis}
          />
        </Spacing>
      </Spacing>
    </section>
  );
};

export const getTextOrExplanation = ({
  groupResult,
  focusAreaDefinition,
  agentReference,
}: {
  groupResult: RootCauseGroupResult;
  focusAreaDefinition: MyFocusAreaDefinition;
  agentReference: string;
}): string => {
  if (isDiversionBarGraphGroupResult(groupResult)) {
    if (groupResult.text !== undefined && groupResult.text !== null) {
      return generateTextFromRootCauseTextOutputs({
        textOutputs: groupResult.text,
        focusAreaDefinition,
        agentReference,
      });
    } else {
      return '';
    }
  }

  if (groupResult.plot_type === 'BinScatterControlledVsUncontrolled') {
    return generateTextFromRootCauseTextOutputs({
      textOutputs: groupResult.explanation,
      focusAreaDefinition,
      agentReference,
    });
  }

  if (groupResult.plot_type === 'TextOnly') {
    return generateTextFromRootCauseTextOutputs({
      textOutputs: groupResult.explanation,
      focusAreaDefinition,
      agentReference,
    });
  }

  if (['barChart', 'lineChart'].includes(groupResult.plot_type)) {
    const textOutputs = extractRootCauseTextOutputs(groupResult);
    if (textOutputs && typeof textOutputs === 'object') {
      return generateTextFromRootCauseTextOutputs({
        textOutputs,
        focusAreaDefinition,
        agentReference,
      });
    }
  }

  return groupResult.text !== undefined && groupResult.text !== null
    ? typeof groupResult.text === 'string'
      ? groupResult.text
      : generateTextFromRootCauseTextOutputs({
          textOutputs: groupResult.text,
          focusAreaDefinition,
          agentReference,
        })
    : groupResult.explanation !== undefined && groupResult.explanation !== null
      ? typeof groupResult.explanation === 'string'
        ? groupResult.explanation
        : generateTextFromRootCauseTextOutputs({
            textOutputs: groupResult.explanation,
            focusAreaDefinition,
            agentReference,
          })
      : '';
};
